import { toPng, type Options as HtmlToImageOptions } from "html-to-image"

export type DomToPngOptions = HtmlToImageOptions & {
  quality?: number
  /** 跨域图片无法读取时，用文字头像替代 */
  letterFallback?: string
}

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
    webkit?: {
      messageHandlers?: {
        SavePhoto?: {
          postMessage: (message: { body: string }) => void
        }
      }
    }
  }
}

const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAgjIdoYAAAAASUVORK5CYII="

const DEFAULT_FILTER_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT"])

export function filterNode(node: HTMLElement): boolean {
  if (DEFAULT_FILTER_TAGS.has(node.tagName)) return false
  if (node.dataset?.noScreenshot !== undefined) return false
  if (node.classList?.contains("no-screenshot")) return false
  return true
}

function isCrossOriginUrl(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin
  } catch {
    return false
  }
}

function tryImageToDataUrl(img: HTMLImageElement): string | null {
  try {
    const w = img.naturalWidth || img.width
    const h = img.naturalHeight || img.height
    if (!w || !h) return null
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, w, h)
    return canvas.toDataURL("image/png")
  } catch {
    return null
  }
}

function createLetterAvatarDataUrl(name: string, size: number): string {
  const canvas = document.createElement("canvas")
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext("2d")
  if (!ctx) return TRANSPARENT_PIXEL

  ctx.fillStyle = "#52c41a"
  ctx.beginPath()
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = "#ffffff"
  ctx.font = `600 ${Math.max(12, Math.floor(size * 0.4))}px sans-serif`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(name?.[0]?.toUpperCase() || "?", size / 2, size / 2)
  return canvas.toDataURL("image/png")
}

/** 截图前把跨域 img 换成 data URL，避免 html-to-image 再次 fetch 触发 CORS */
export async function inlineImagesForCapture(
  root: HTMLElement,
  options: { letterFallback?: string } = {},
): Promise<() => void> {
  const restores: Array<() => void> = []
  const imgs = Array.from(root.querySelectorAll("img"))

  for (const img of imgs) {
    const originalSrc = img.currentSrc || img.src
    const originalSrcset = img.srcset

    if (!originalSrc || originalSrc.startsWith("data:") || originalSrc.startsWith("blob:")) {
      continue
    }

    if (!isCrossOriginUrl(originalSrc)) {
      continue
    }

    const size = img.width || img.naturalWidth || 60
    const dataUrl =
      tryImageToDataUrl(img) ??
      (options.letterFallback
        ? createLetterAvatarDataUrl(options.letterFallback, size)
        : TRANSPARENT_PIXEL)

    img.srcset = ""
    img.src = dataUrl

    restores.push(() => {
      img.src = originalSrc
      img.srcset = originalSrcset
    })
  }

  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))

  return () => restores.forEach((fn) => fn())
}

export function domToPng(node: HTMLElement, options: DomToPngOptions = {}): Promise<string> {
  const {
    quality: _quality,
    filter = filterNode,
    backgroundColor,
    pixelRatio,
    cacheBust,
    letterFallback: _letterFallback,
    ...rest
  } = options

  return toPng(node, {
    filter,
    backgroundColor,
    pixelRatio: pixelRatio ?? Math.min(window.devicePixelRatio || 1, 2),
    cacheBust: cacheBust ?? false,
    imagePlaceholder: TRANSPARENT_PIXEL,
    onImageErrorHandler: () => undefined,
    ...rest,
  })
}

export function downloadImageAsync(dataUrl: string, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const name = filename.includes(".") ? filename : `${filename}.png`
      const link = document.createElement("a")
      link.href = dataUrl
      link.download = name
      link.rel = "noopener"
      link.style.display = "none"
      document.body.appendChild(link)
      link.click()
      setTimeout(() => link.remove(), 100)
      resolve()
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)))
    }
  })
}

export function handleScreenshot(
  ref: HTMLElement | null | undefined,
  filename = `screenshot-${Date.now()}`,
  options: DomToPngOptions = {},
  fileType = "png",
): Promise<string> {
  if (!ref) {
    return Promise.reject(new Error("截图区域未就绪"))
  }

  const { letterFallback, ...domOptions } = options

  return inlineImagesForCapture(ref, { letterFallback })
    .then((restore) =>
      domToPng(ref, { quality: 1, filter: filterNode, ...domOptions })
        .finally(restore),
    )
    .then((dataUrl) => {
      if (!dataUrl) {
        throw new Error("截图生成失败")
      }

      const fullName = filename.includes(".") ? filename : `${filename}.${fileType}`

      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "savePhotoByBase64",
            data: {
              base64: dataUrl,
              filename: fullName,
            },
          }),
        )
        return filename
      }

      if (window.webkit?.messageHandlers?.SavePhoto?.postMessage) {
        window.webkit.messageHandlers.SavePhoto.postMessage({ body: dataUrl })
        return filename
      }

      return downloadImageAsync(dataUrl, fullName).then(() => filename)
    })
    .catch((error) => {
      throw error instanceof Error ? error : new Error(String(error))
    })
}
